const MAX_FLOW_JSON_BYTES = 1024 * 1024;

const countComponents = nodes =>
  nodes.reduce(
    (total, node) =>
      total +
      1 +
      countComponents(Array.isArray(node?.children) ? node.children : []),
    0
  );

export const parseFlowJson = (source, t = key => key) => {
  const text = typeof source === 'string' ? source.trim() : '';
  if (!text) throw new Error(t('ACTIVE_MESSAGES.FLOW_JSON_ERRORS.EMPTY'));
  if (new Blob([text]).size > MAX_FLOW_JSON_BYTES) {
    throw new Error(t('ACTIVE_MESSAGES.FLOW_JSON_ERRORS.TOO_LARGE'));
  }

  let schema;
  try {
    schema = JSON.parse(text);
  } catch {
    throw new Error(t('ACTIVE_MESSAGES.FLOW_JSON_ERRORS.INVALID_JSON_GENERIC'));
  }

  if (!schema || Array.isArray(schema) || typeof schema !== 'object') {
    throw new Error(t('ACTIVE_MESSAGES.FLOW_JSON_ERRORS.ROOT_OBJECT'));
  }
  if (typeof schema.version !== 'string' || !schema.version.trim()) {
    throw new Error(t('ACTIVE_MESSAGES.FLOW_JSON_ERRORS.VERSION'));
  }
  if (!Array.isArray(schema.screens) || schema.screens.length === 0) {
    throw new Error(t('ACTIVE_MESSAGES.FLOW_JSON_ERRORS.SCREENS'));
  }

  const ids = new Set();
  schema.screens.forEach((screen, index) => {
    if (!screen?.id || typeof screen.id !== 'string') {
      throw new Error(
        t('ACTIVE_MESSAGES.FLOW_JSON_ERRORS.SCREEN_ID_REQUIRED', {
          index: index + 1,
        })
      );
    }
    if (ids.has(screen.id)) {
      throw new Error(
        t('ACTIVE_MESSAGES.FLOW_JSON_ERRORS.DUPLICATE_SCREEN_ID', {
          id: screen.id,
        })
      );
    }
    ids.add(screen.id);
    if (screen.layout?.type !== 'SingleColumnLayout') {
      throw new Error(
        t('ACTIVE_MESSAGES.FLOW_JSON_ERRORS.LAYOUT_TYPE', { id: screen.id })
      );
    }
    if (!Array.isArray(screen.layout.children)) {
      throw new Error(
        t('ACTIVE_MESSAGES.FLOW_JSON_ERRORS.LAYOUT_CHILDREN', { id: screen.id })
      );
    }
  });

  return schema;
};

export const getFlowSummary = schema => {
  if (!schema?.screens?.length) return null;

  return {
    version: schema.version,
    dataApiVersion: schema.data_api_version || null,
    screens: schema.screens.length,
    components: schema.screens.reduce(
      (total, screen) => total + countComponents(screen.layout.children),
      0
    ),
    terminalScreens: schema.screens.filter(screen => screen.terminal).length,
  };
};

export const getInitialFlowScreen = (schema, preferredScreenId) => {
  const screens = schema?.screens || [];
  return (
    screens.find(screen => screen.id === preferredScreenId) ||
    screens[0] ||
    null
  );
};

export const resolveFlowText = (value, data = {}) => {
  if (typeof value !== 'string') return value;

  return value.replace(
    /\$\{(?:screen\.[^.]+\.form|data)\.([^}]+)\}/g,
    (_, key) => {
      const resolved = data[key];
      return resolved === undefined || resolved === null
        ? ''
        : String(resolved);
    }
  );
};
